<template>
  <div class="pa-1 mt-4">
    <!-- Desktop View -->
    <v-data-table
      v-if="!device.isMobile"
      :headers="headers"
      :items="restaurantSummary"
      class="striped-table ordera-box-shadow"
      hide-default-footer
      disable-pagination
      mobile-breakpoint="0"
      :loading="loading"
    >
      <template v-slot:no-data>
        <v-container class="text-center py-12">
          <v-icon size="64" color="grey lighten-1" class="mb-4">mdi-store-off</v-icon>
          <div class="text-h6 grey--text text--darken-1 mb-2">
            {{ $t("no_data") }}
          </div>
          <div class="text-body-2 grey--text">
            {{ $t("select_date_and_filters") }}
          </div>
        </v-container>
      </template>

      <template v-slot:item.num="{ item }">
        {{ item.num }}
      </template>

      <template v-slot:item.restaurant="{ item }">
        <div class="d-flex align-center">
          <v-icon small class="mr-2" :color="item.isTotal ? 'primary' : 'orange'">
            {{ item.isTotal ? "mdi-sigma" : "mdi-store" }}
          </v-icon>
          <span :class="item.isTotal ? 'font-weight-bold' : 'font-weight-medium'">
            {{ toLatin(item.restaurant) }}
          </span>
        </div>
      </template>

      <template v-slot:item.meal_count="{ item }">
        <span :class="item.isTotal ? 'font-weight-bold' : ''">
          {{ item.meal_count }}
        </span>
      </template>

      <template v-slot:item.total_amount="{ item }">
        <span :class="item.isTotal ? 'font-weight-bold' : ''">
          {{ formatCurrency(item.total_amount) }}
        </span>
      </template>

      <template v-slot:item.company_amount="{ item }">
        <span :class="item.isTotal ? 'font-weight-bold' : ''">
          {{ formatCurrency(item.company_amount) }}
        </span>
      </template>

      <template v-slot:item.employee_amount="{ item }">
        <span :class="item.isTotal ? 'font-weight-bold' : ''">
          {{ formatCurrency(item.employee_amount) }}
        </span>
      </template>
    </v-data-table>

    <!-- Mobile View -->
    <mobile-data-cards
      v-else
      :headers="mobileHeaders"
      :items="restaurantSummary"
      :loading="loading"
      item-key="id"
      :fields-per-row="2"
      :bold-fields="['restaurant']"
      :show-row-number="false"
      class="px-3"
    >
      <template v-slot:item.restaurant="{ item }">
        <div class="d-flex align-center">
          <v-icon small class="mr-2" :color="item.isTotal ? 'primary' : 'orange'">
            {{ item.isTotal ? "mdi-sigma" : "mdi-store" }}
          </v-icon>
          {{ toLatin(item.restaurant) }}
        </div>
      </template>
      <template v-slot:item.total_amount="{ item }">
        {{ formatCurrency(item.total_amount) }}
      </template>
      <template v-slot:item.company_amount="{ item }">
        {{ formatCurrency(item.company_amount) }}
      </template>
      <template v-slot:item.employee_amount="{ item }">
        {{ formatCurrency(item.employee_amount) }}
      </template>
    </mobile-data-cards>
  </div>
</template>

<script>
import limitService from "@/services/limit";
import hasSnackbar from "@/mixins/hasSnackbar";
import MobileDataCards from "@/components/global/MobileDataCards.vue";
import hasPrinting from "@/mixins/hasPrinting";
import hasReportPrinting from "@/mixins/report/hasReportPrinting";
import hasExcel from "@/mixins/hasExcel";

export default {
  name: "CompanyMultiRestaurantTabRestaurantSummary",
  components: {
    MobileDataCards,
  },
  mixins: [hasSnackbar, hasPrinting, hasReportPrinting, hasExcel],
  data: () => ({
    report: null,
    loading: false,
  }),
  computed: {
    filters() {
      return this.$store.state.report.filters;
    },
    dates() {
      const { from, to } = this.$store.state.report.filters.dates;
      return { from, to };
    },
    selectedRestaurants() {
      return this.$store.state.report.selectedRestaurants || [];
    },
    restaurants() {
      return this.$store.state.company?.restaurants || [];
    },
    companyId() {
      return this.$store.state.company?.id || null;
    },
    restaurantSummary() {
      const userGroups = Array.isArray(this.report) ? this.report : [];
      const byRestaurant = {};

      userGroups.forEach((group) => {
        (group.limitTransactions || []).forEach((t) => {
          if (t.company_id !== this.companyId) return;
          if (!t.restaurant_id) return;
          if (this.selectedRestaurants.length > 0 && !this.selectedRestaurants.includes(t.restaurant_id)) return;
          const txDate = (t.date || '').substring(0, 10);
          if (txDate < this.dates.from || txDate > this.dates.to) return;

          if (!byRestaurant[t.restaurant_id]) {
            byRestaurant[t.restaurant_id] = {
              restaurant_name: t.restaurant?.name || String(t.restaurant_id),
              meal_count: 0,
              total_amount: 0,
              company_amount: 0,
              employee_amount: 0,
            };
          }

          const entry = byRestaurant[t.restaurant_id];
          const amount = Math.abs(parseFloat(t.total_amount ?? t.price) || 0);

          if (t.orderable_id !== null) {
            entry.meal_count += parseInt(t.quantity) || 1;
            entry.total_amount += amount;
            entry.company_amount += parseFloat(t.order?.company_paid_amount) || amount;
            entry.employee_amount += parseFloat(t.order?.user_paid_amount) || 0;
          }
        });
      });

      const restaurantIds = this.selectedRestaurants.length > 0
        ? this.selectedRestaurants
        : Object.keys(byRestaurant).map(Number);

      let totalMealCount = 0;
      let totalAmount = 0;
      let totalCompanyAmount = 0;
      let totalEmployeeAmount = 0;

      const summary = restaurantIds.map((restaurantId, index) => {
        const restaurant = this.restaurants.find((r) => r.id === restaurantId);
        const entry = byRestaurant[restaurantId] || {};
        totalMealCount += entry.meal_count || 0;
        totalAmount += entry.total_amount || 0;
        totalCompanyAmount += entry.company_amount || 0;
        totalEmployeeAmount += entry.employee_amount || 0;
        return {
          id: restaurantId,
          num: index + 1,
          restaurant: restaurant?.name || entry.restaurant_name || String(restaurantId),
          meal_count: entry.meal_count || 0,
          total_amount: entry.total_amount || 0,
          company_amount: entry.company_amount || 0,
          employee_amount: entry.employee_amount || 0,
          isTotal: false,
        };
      });

      if (summary.length > 0) {
        summary.push({
          id: "total",
          num: "",
          restaurant: this.$t("total").toUpperCase(),
          meal_count: totalMealCount,
          total_amount: totalAmount,
          company_amount: totalCompanyAmount,
          employee_amount: totalEmployeeAmount,
          isTotal: true,
        });
      }

      return summary;
    },
    headers() {
      return [
        {
          text: this.$t("r_b"),
          value: "num",
          sortable: false,
          width: "80px",
          print: false,
        },
        { text: this.$t("restaurant"), value: "restaurant", sortable: false },
        {
          text: this.$t("meal_count"),
          value: "meal_count",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("amount") + ` ${this.restaurantCurrency}`,
          value: "total_amount",
          align: "right",
          sortable: false,
        },
        {
          text: this.$t("company_amount") + ` ${this.restaurantCurrency}`,
          value: "company_amount",
          align: "right",
          sortable: false,
        },
        {
          text: this.$t("employee_amount") + ` ${this.restaurantCurrency}`,
          value: "employee_amount",
          align: "right",
          sortable: false,
        },
      ];
    },
    mobileHeaders() {
      return [
        { text: this.$t("restaurant"), value: "restaurant" },
        { text: this.$t("meal_count"), value: "meal_count" },
        {
          text: this.$t("amount") + ` ${this.restaurantCurrency}`,
          value: "total_amount",
        },
        {
          text: this.$t("company_amount") + ` ${this.restaurantCurrency}`,
          value: "company_amount",
        },
        {
          text: this.$t("employee_amount") + ` ${this.restaurantCurrency}`,
          value: "employee_amount",
        },
      ];
    },
  },
  methods: {
    async fetchData() {
      if (!this.companyId) {
        this.report = null;
        return;
      }
      this.loading = true;
      this.report = null;
      try {
        const { data } = await limitService.getCompanyTransactions(this.companyId, { date: this.dates.from, date2: this.dates.to });
        this.report = data;
      } catch (error) {
        this.showErrorSnackbar(error, this.$t("error_loading_report"));
      } finally {
        this.loading = false;
      }
    },
    formatCurrency(amount) {
      return (parseFloat(amount) || 0).toFixed(2);
    },
    getExportHeaders() {
      return this.headers.filter((h) => h.print !== false);
    },
    formatSummaryForExport(item) {
      const moneyFields = ["total_amount", "company_amount", "employee_amount"];
      const exportItem = {};
      this.getExportHeaders().forEach((h) => {
        if (h.value === "restaurant") exportItem[h.value] = this.toLatin(item[h.value]);
        else if (moneyFields.includes(h.value))
          exportItem[h.value] = this.formatCurrency(item[h.value]);
        else exportItem[h.value] = item[h.value] || "";
      });
      return exportItem;
    },
  },
  watch: {
    companyId() {
      this.fetchData();
    },
    dates: {
      handler() {
        this.fetchData();
      },
      deep: true,
    },
    isExcelDownloading() {
      if (!this.isExcelDownloading) return;
      this.downloadExcel({
        fileName: [this.$t("report"), this.$t("restaurant_summary")].join(" ") + ".xlsx",
        sheets: [
          {
            name: this.$t("restaurant_summary"),
            headers: this.getExportHeaders(),
            items: this.restaurantSummary.map((item) =>
              this.formatSummaryForExport(item)
            ),
            title: [this.$t("report"), this.$t("restaurant_summary")].join(" "),
            subtitle: this.subtitle,
          },
        ],
      });
    },
    isPrinting(isPrinting) {
      if (!isPrinting) return;
      this.printDocument({
        tables: [
          {
            headers: this.getExportHeaders(),
            items: this.restaurantSummary.map((item) =>
              this.formatSummaryForExport(item)
            ),
            showCaption: true,
          },
        ],
        title: [this.$t("report"), this.$t("restaurant_summary")].join(": "),
        subtitle: this.subtitle,
        hideDatetime: true,
      });
    },
  },
  mounted() {
    this.fetchData();
  },
};
</script>
